import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authUtils } from '../../services/auth';
import { apiService } from '../../services/api';
import RelativeTime from '../common/RelativeTime';
import { formatDateTime } from '../../utils/dates';
import './PracticeRunHistory.css';

/**
 * Practice run history
 *
 * The caller's past practice runs, newest first: per-task average points and a
 * per-row log download. Self-hiding - renders nothing when the list is empty, so
 * competitions without practice runs (and every non-comp-15 competition) are
 * unaffected.
 *
 * ONE ROW PER JOB, not per run. Each task cell is the mean over that job's N
 * runs in the task's own scale (e.g. 1.8 / 2), which is what keeps the table
 * four columns wide whether the job ran 1 or 3 runs per task, and whether it
 * finished or was canceled part-way. Those runs execute in parallel, so a job
 * is one wall-clock window however many repeats it asked for.
 *
 * No polling loop of its own: the competition page bumps `refreshSignal` when a
 * run starts, is canceled, or reaches a terminal state (LiveTerminals already
 * polls that state and reports it upward). The deferred refetch below is a
 * safety valve for a row that is still live when the list is fetched; it
 * re-arms while anything is unfinished, and pauses while the tab is hidden.
 */

interface TaskCell {
  task: string;
  label: string;
  /** null when this task produced nothing - render a dash, never a zero. */
  avg_points: number | null;
  max: number | null;
  runs: number | null;
  /** Tokens are the leaderboard tie-breaker, so they belong next to the
   *  points at the same granularity. Totals for the task, and the per-run
   *  mean, which is the figure that compares across submissions. */
  tokens_in: number | null;
  tokens_out: number | null;
  avg_tokens_in: number | null;
  avg_tokens_out: number | null;
  model_requests: number | null;
}

interface PracticeRun {
  job_id: string;
  created_at: string | null;
  n_runs: number | null;
  state: string;
  finished: boolean;
  score: number | null;
  error: string | null;
  canceled_at: string | null;
  /** A canceled run only has logs if its terminals were snapshotted in time. */
  has_logs: boolean;
  /** Whole-job totals, so the row need not be summed by eye. */
  tokens_in: number | null;
  tokens_out: number | null;
  /** Per-RUN means across the whole protocol. The total says what this job
   *  spent; the mean says what one pass costs, which is the figure that
   *  compares against a submission that ran a different number of runs. */
  avg_tokens_in: number | null;
  avg_tokens_out: number | null;
  /** Wall time for the whole job, from pulsar. */
  duration_seconds: number | null;
  tasks: TaskCell[];
  /** Every (task, run) individually. The per-task mean cannot answer "why did
   *  run 2 score half of run 3"; this is what the expanded row shows. */
  runs: RunDetail[];
}

interface RunDetail {
  task: string;
  run: number | null;
  points: number | null;
  max: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  model_requests: number | null;
  wall_seconds: number | null;
  notes: string | null;
}

interface PracticeRunHistoryProps {
  competitionId: number;
  /** Bump to refetch. */
  refreshSignal?: number;
  /**
   * Reports whether this user has ANY run at all, of any kind. The page uses it
   * to disable "Download run logs" for someone who has never submitted -- a
   * graded run has no live-terminals card, so that is the only signal available
   * without opening a second request.
   */
  onHasAnyRun?: (hasAnyRun: boolean) => void;
}

const LIVE_REFETCH_MS = 10000;

// Mirrors the pill vocabulary the live-terminals card already uses, so a run
// looks the same in both places -- including `canceled`, which used to share
// the grey "finished" pill with a successful run and so was indistinguishable
// from one at a glance.
const pillKind = (state: string): string => {
  if (state === 'failed' || state === 'error') return 'failed';
  if (state === 'canceled') return 'canceled';
  if (state === 'succeeded') return 'finished';
  if (state === 'running') return 'running';
  return 'queued';
};

/** The one line a row expands to show. Every row has one, so no state gets a
 *  chevron the others lack -- a failure should not be the only thing that looks
 *  different. */
const hasDetail = (run: PracticeRun): boolean =>
  Boolean(rowDetail(run)) || (run.runs?.length ?? 0) > 0;

// America/Chicago, like the terminals, which stamp Chicago server-side; two clocks on
// the same page must agree.
const RUN_TIME_ZONE = 'America/Chicago';

const rowDetail = (run: PracticeRun): string | null => {
  if (run.state === 'canceled') {
    const when = run.canceled_at ? formatDateTime(run.canceled_at, '', RUN_TIME_ZONE) : null;
    return when ? `You canceled this run at ${when}.` : 'You canceled this run.';
  }
  if (run.error) return run.error;
  if (!run.finished) return 'Still running.';
  // A run that simply succeeded has nothing to add: every number was already
  // in the row, and repeating it under the row was pure noise.
  if (run.state === 'succeeded') return null;
  return 'No further detail for this run.';
};

/** 1234 -> "1.2k". Columns are narrow; the exact figure is in the tooltip. */
const fmtTokens = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n) || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

/** Rows shown before the "show all" toggle. */
const RECENT_RUNS = 10;

/** 93.4 -> "1m 33s". Bare seconds stop being readable past a minute. */
const fmtElapsed = (sec: number | null | undefined): string => {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '-';
  const whole = Math.round(sec);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s2 = whole % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  return m ? `${m}m ${String(s2).padStart(2, '0')}s` : `${s2}s`;
};

const formatPoints = (value: number | null): string => {
  if (value == null) return '-';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const PracticeRunHistory: React.FC<PracticeRunHistoryProps> = ({
  competitionId,
  refreshSignal,
  onHasAnyRun,
}) => {
  const [runs, setRuns] = useState<PracticeRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  // Show the recent runs and offer the rest. After a week of iteration this
  // table runs to dozens of rows, and the problem then is FINDING the good run
  // -- which is why this is a default view, not a delete or hide feature.
  // Deleting would trade away the only record of what a submission did, and
  // nobody curates 47 rows by hand.
  const [showAll, setShowAll] = useState(false);
  const deferredRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchRuns = useCallback(async () => {
    if (!competitionId) return;
    try {
      const response = await apiService.get(`/evaluations/${competitionId}/practice/runs`, {
        headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` },
      });
      const payload = response.data as { runs?: PracticeRun[]; has_any_run?: boolean };
      setRuns(Array.isArray(payload?.runs) ? payload.runs : []);
      if (typeof payload?.has_any_run === 'boolean') onHasAnyRun?.(payload.has_any_run);
      setError('');
    } catch (err: any) {
      // apiService errors are flat: { status, message, code } - no err.response.
      setError(err?.message || 'Failed to load your practice runs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns, refreshSignal]);

  // Safety valve only. If a row is still live, look again once; the page's
  // refreshSignal is the primary path and normally lands first.
  // `runs` is a fresh array on every fetch, so depending on it made this
  // re-arm forever -- a poll wearing a one-shot's name. Key it on the only
  // thing that actually matters, and stop while nobody is looking.
  const hasLiveRun = runs.some((run) => !run.finished);
  useEffect(() => {
    if (deferredRef.current) clearTimeout(deferredRef.current);
    if (!hasLiveRun) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') fetchRuns();
      deferredRef.current = setTimeout(tick, LIVE_REFETCH_MS);
    };
    deferredRef.current = setTimeout(tick, LIVE_REFETCH_MS);
    return () => {
      if (deferredRef.current) clearTimeout(deferredRef.current);
    };
  }, [hasLiveRun, fetchRuns]);

  const handleDownload = async (run: PracticeRun) => {
    try {
      setDownloadingJobId(run.job_id);
      setDownloadError('');
      const response = await apiService.get(
        `/evaluations/${competitionId}/practice/runs/${run.job_id}/logs`,
        { headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` } }
      );
      const { url } = response.data as { url: string };
      // Not target="_blank": this click happens after an await, so it has
      // lost user activation and Safari and Firefox block the popup silently.
      // The participant sees "..." and then nothing at all. A same-tab
      // navigation to a presigned URL downloads without needing activation.
      window.location.assign(url);
    } catch (err: any) {
      setDownloadError(err?.message || 'Could not fetch those run logs');
    } finally {
      setDownloadingJobId(null);
    }
  };

  // Self-hiding: nothing to say until there is history. Also covers the first
  // load, so the card never flashes an empty shell.
  if (loading || (!runs.length && !error)) return null;

  return (
    <section className="practice-history" data-tour="practice-history">
      <div className="practice-history__head">
        <h2 className="practice-history__title">Your practice runs</h2>
        <span className="practice-history__hint">
          Practice runs never affect the leaderboard. Per-task points are in each
          task&apos;s own scale.
        </span>
      </div>

      {error && <div className="practice-history__error">{error}</div>}
      {downloadError && <div className="practice-history__error">{downloadError}</div>}

      <div className="practice-history__scroll">
        <table className="practice-history__table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Runs</th>
              <th>Status</th>
              <th>Score</th>
              <th>Elapsed</th>
              <th>Tokens</th>
              {(runs[0]?.tasks ?? []).map((task) => (
                <th key={task.task} title={task.label}>
                  {task.label.replace(/^Task (\d+):.*$/, 'Task $1')}
                </th>
              ))}
              <th aria-label="Logs" />
            </tr>
          </thead>
          <tbody>
            {(showAll ? runs : runs.slice(0, RECENT_RUNS)).map((run) => {
              const isOpen = expanded === run.job_id;
              const canDownload = run.state === 'canceled' ? run.has_logs : run.finished;
              return (
                <React.Fragment key={run.job_id}>
                  <tr
                    className={`practice-history__row${isOpen ? ' practice-history__row--open' : ''}`}
                    onClick={() => hasDetail(run) && setExpanded(isOpen ? null : run.job_id)}
                    title={hasDetail(run) ? 'Click for per-run detail' : undefined}
                  >
                    <td>
                      {/* Only rows that HAVE detail get the affordance. A
                          chevron that expands to nothing is a small lie. */}
                      <span className="practice-history__chevron" aria-hidden="true">
                        {hasDetail(run) ? (isOpen ? '\u02c5' : '\u203a') : '\u00a0'}
                      </span>
                      <RelativeTime value={run.created_at} timeZone={RUN_TIME_ZONE} />
                    </td>
                    <td>{run.n_runs ?? '-'}</td>
                    <td>
                      <span className={`lt-pill lt-pill--${pillKind(run.state)}`}>
                        <span className="lt-pill__dot" aria-hidden="true" />
                        <span>{run.state}</span>
                      </span>
                    </td>
                    <td className="practice-history__score">
                      {run.score == null ? '-' : `${Math.round(run.score)}/100`}
                    </td>
                    <td className="practice-history__elapsed">
                      {fmtElapsed(run.duration_seconds)}
                    </td>
                    <td
                      className="practice-history__total-tokens"
                      title={
                        run.tokens_in == null && run.tokens_out == null
                          ? 'No model usage recorded for this run'
                          : `${run.tokens_in ?? 0} new input + ${run.tokens_out ?? 0} output tokens across the whole run`
                      }
                    >
                      {run.tokens_in == null && run.tokens_out == null ? (
                        '-'
                      ) : (
                        <>
                          {`${fmtTokens(run.tokens_in)}\u2191 ${fmtTokens(run.tokens_out)}\u2193`}
                          {/* The mean matters as much as the total once a job
                              runs the protocol three times: the total is what
                              this job spent, the mean is what one pass costs. */}
                          {(run.n_runs ?? 1) > 1 && run.avg_tokens_in != null && (
                            <span className="practice-history__tokens">
                              {`avg ${fmtTokens(run.avg_tokens_in)}\u2191 ${
                                fmtTokens(run.avg_tokens_out)}\u2193 / run`}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    {run.tasks.map((task) => (
                      <td
                        key={task.task}
                        title={
                          task.runs
                            ? `${task.label} - average over ${task.runs} run(s); `
                              + `${task.tokens_in ?? 0} new input + ${task.tokens_out ?? 0} output tokens `
                              + `over ${task.model_requests ?? 0} model call(s)`
                            : `${task.label} - no result`
                        }
                      >
                        {task.avg_points == null ? (
                          '-'
                        ) : (
                          <>
                            {`${formatPoints(task.avg_points)} / ${formatPoints(task.max)}`}
                            {/* Tokens under the points: the tie-breaker at the
                                same granularity as the score it breaks. */}
                            {(task.tokens_in != null || task.tokens_out != null) && (
                              <span className="practice-history__tokens">
                                {fmtTokens((task.runs ?? 1) > 1 ? task.avg_tokens_in : task.tokens_in)}
                                {'\u2191 '}
                                {fmtTokens((task.runs ?? 1) > 1 ? task.avg_tokens_out : task.tokens_out)}
                                {'\u2193'}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    ))}
                    <td>
                      <button
                        type="button"
                        className="practice-history__logs-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(run);
                        }}
                        disabled={!canDownload || downloadingJobId === run.job_id}
                        title={
                          canDownload
                            ? "Download this run's full logs"
                            : run.state === 'canceled'
                              ? 'Canceled before it started - no logs were produced'
                              : 'Available once the run finishes'
                        }
                      >
                        {downloadingJobId === run.job_id ? '...' : 'Logs'}
                      </button>
                    </td>
                  </tr>
                  {isOpen && hasDetail(run) && (
                    <tr className="practice-history__detail-row">
                      <td colSpan={7 + run.tasks.length}>
                        {rowDetail(run) && <p className="practice-history__note">{rowDetail(run)}</p>}
                        {(run.runs?.length ?? 0) > 0 && (
                          <table className="practice-history__runs">
                            <thead>
                              <tr>
                                <th>Task</th>
                                <th>Run</th>
                                <th>Score</th>
                                <th>In tok</th>
                                <th>Out tok</th>
                                <th>Calls</th>
                              </tr>
                            </thead>
                            <tbody>
                              {run.runs.map((r) => (
                                <tr key={`${r.task}-${r.run}`}>
                                  <td>{r.task.replace(/^\d+_/, '').replace(/_/g, ' ')}</td>
                                  <td>{r.run ?? '-'}</td>
                                  <td>
                                    {r.points == null
                                      ? '-'
                                      : `${formatPoints(r.points)} / ${formatPoints(r.max)}`}
                                  </td>
                                  <td>{fmtTokens(r.tokens_in)}</td>
                                  <td>{fmtTokens(r.tokens_out)}</td>
                                  <td>{r.model_requests ?? '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {runs.length > RECENT_RUNS && (
          <button
            type="button"
            className="practice-history__more"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll
              ? `Show only the last ${RECENT_RUNS}`
              : `Show all ${runs.length} runs`}
          </button>
        )}
      </div>
    </section>
  );
};

export default PracticeRunHistory;
